import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shield, ArrowLeft, CheckCircle2, XCircle, RotateCw, Gift, Ticket, Copy, Check, HeartPulse, AlertTriangle, Handshake, DollarSign, Zap } from "lucide-react";
import { toast } from "sonner";

// Owner-only page to restore any customer's Stripe subscription state to Mongo.
// Backend gates by ADMIN_EMAILS env var — non-admins hit a 403 and see a clear error.
export default function AdminRestore() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Manual-grant controls (independent from reconcile).
  const [grantDays, setGrantDays] = useState(30);
  const [granting, setGranting] = useState(false);

  // Mint promo code controls
  const [mintLabel, setMintLabel] = useState("Lifetime Pro (giveaway)");
  const [minting, setMinting] = useState(false);
  const [mintedList, setMintedList] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [copiedCode, setCopiedCode] = useState("");

  // Customer health check
  const [healthEmail, setHealthEmail] = useState("");
  const [checking, setChecking] = useState(false);
  const [healthResult, setHealthResult] = useState(null);

  // Partner (influencer) admin controls
  const [partners, setPartners] = useState([]);
  const [newPartner, setNewPartner] = useState({ code: "", name: "", email: "" });
  const [creatingPartner, setCreatingPartner] = useState(false);

  const loadPartners = async () => {
    try {
      const r = await api.get("/admin/partners");
      setPartners(r.data?.partners || []);
    } catch (_e) { /* 403 for non-admins is silent */ }
  };
  useEffect(() => { loadPartners(); }, []);

  const createPartner = async () => {
    const code = newPartner.code.trim().toUpperCase();
    const name = newPartner.name.trim();
    if (!code || !name) { toast.error("Code and name required"); return; }
    setCreatingPartner(true);
    try {
      const r = await api.post("/admin/partners", {
        code, name, email: newPartner.email.trim().toLowerCase() || null,
      });
      toast.success(`Partner ${r.data.partner.code} created — share link: /?ref=${r.data.partner.code}`);
      setNewPartner({ code: "", name: "", email: "" });
      await loadPartners();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create partner");
    } finally { setCreatingPartner(false); }
  };

  const markPartnerPaid = async (code) => {
    if (!window.confirm(`Mark ALL pending commissions for ${code} as paid?`)) return;
    try {
      const r = await api.post(`/admin/partners/${code}/mark-paid`);
      toast.success(`Marked ${r.data.commissions_marked_paid} commissions as paid`);
      await loadPartners();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const checkHealth = async () => {
    const e = healthEmail.trim().toLowerCase();
    if (!e) { toast.error("Enter an email"); return; }
    setChecking(true);
    setHealthResult(null);
    try {
      const r = await api.get(`/admin/customer-health?email=${encodeURIComponent(e)}`);
      setHealthResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lookup failed");
    } finally {
      setChecking(false);
    }
  };

  const loadMintedCodes = async () => {
    try {
      const r = await api.get("/admin/mint-codes?limit=25");
      setMintedList(r.data?.codes || []);
    } catch (_e) {
      // Silent — 403 for non-admins is expected and already handled elsewhere.
    }
  };

  // Load ALL promo redemptions (both hardcoded and minted codes) so the admin
  // can see who signed up with e.g. TGPROFOREVER — not just minted codes.
  const loadRedemptions = async () => {
    try {
      const r = await api.get("/admin/redemptions?limit=50");
      setRedemptions(r.data?.redemptions || []);
    } catch (_e) { /* non-admin ignores */ }
  };

  // Bunny Stream CDN migration state — lets the admin push existing clips to
  // Bunny's global CDN so playback is snappy (~1-2s to first frame) instead
  // of the slower backend stream fallback.
  const [bunnyStatus, setBunnyStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateDays, setMigrateDays] = useState(30);

  const loadBunnyStatus = async () => {
    try {
      const r = await api.get("/admin/video/bunny-status");
      setBunnyStatus(r.data);
    } catch (_e) { /* non-admin or not configured */ }
  };

  // Force a fresh poll of Bunny for every pending clip, then reload the stats.
  // Used by the REFRESH button so admins see accurate "Ready to Play" counts
  // instead of the DB's cached values (which lag until clips get opened or
  // the 90-second background poller fires).
  const refreshBunnyLive = async () => {
    try {
      await api.post("/admin/video/refresh-bunny-status");
    } catch (_e) { /* silent — fall back to just re-reading the DB */ }
    await loadBunnyStatus();
  };

  const kickoffMigration = async () => {
    if (!window.confirm(`Migrate all clips from the last ${migrateDays} days to Bunny CDN? This runs in the background and can take a few minutes for larger libraries.`)) return;
    setMigrating(true);
    try {
      const r = await api.post(`/admin/video/migrate-to-bunny?days=${migrateDays}`);
      toast.success(`Queued ${r.data.queued} clip${r.data.queued === 1 ? "" : "s"} for CDN upload. Refresh in ~30s to see progress.`);
      // Live-poll Bunny at increasing intervals so the admin sees the numbers
      // tick up as uploads and encodes complete.
      setTimeout(refreshBunnyLive, 5000);
      setTimeout(refreshBunnyLive, 20000);
      setTimeout(refreshBunnyLive, 60000);
      setTimeout(refreshBunnyLive, 180000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start migration");
    } finally {
      setMigrating(false);
    }
  };

  useEffect(() => { loadMintedCodes(); loadRedemptions(); loadBunnyStatus(); }, []);

  const mintCode = async () => {
    setMinting(true);
    try {
      const r = await api.post("/admin/mint-code", { label: mintLabel.trim() || "Lifetime Pro (minted)" });
      toast.success(`Minted ${r.data.code}`);
      // Refresh list; put new one on top too so it's instant.
      setMintedList((prev) => [r.data, ...prev]);
      // Auto-copy the fresh code so the admin can paste it directly.
      try {
        await navigator.clipboard.writeText(r.data.code);
        setCopiedCode(r.data.code);
        setTimeout(() => setCopiedCode(""), 2000);
      } catch (_e) { /* clipboard blocked — no-op */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mint failed");
    } finally {
      setMinting(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(""), 2000);
    } catch (_e) {
      toast.error("Clipboard blocked");
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const body = { email: email.trim().toLowerCase() };
      if (customerId.trim()) body.stripe_customer_id = customerId.trim();
      const r = await api.post("/admin/reconcile-by-email", body);
      setResult({ ok: true, data: r.data });
      if (r.data?.billing?.has_access) {
        toast.success(`${email} is now restored — access active.`);
      } else {
        toast(`Reconciled but Stripe reports no active subscription. See details below.`, { icon: "🔍" });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Restore failed";
      setResult({ ok: false, error: detail, status: err?.response?.status });
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const grant = async () => {
    if (!email.trim() || !grantDays) return;
    if (!window.confirm(`Grant ${grantDays} days of access to ${email}? This bypasses Stripe.`)) return;
    setGranting(true);
    try {
      const r = await api.post("/admin/grant-days", { email: email.trim().toLowerCase(), days: Number(grantDays) });
      toast.success(`Granted ${grantDays} days to ${email}. New expiry: ${r.data?.subscribed_until?.slice(0, 10)}`);
      setResult({ ok: true, data: r.data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Grant failed");
    } finally {
      setGranting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--fg)", fontFamily: "var(--font-body)" }}>
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 900px 500px at 8% 5%, rgba(34,197,94,0.08), transparent 60%),
            radial-gradient(ellipse 900px 500px at 92% 105%, rgba(139,92,246,0.10), transparent 60%)
          `,
        }}
      />
      <div className="relative max-w-2xl mx-auto px-8 py-10">
        <button
          onClick={() => navigate("/")}
          data-testid="admin-back-btn"
          className="flex items-center gap-2 text-xs mb-8 transition-colors"
          style={{ color: "var(--fg-3)" }}
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> BACK TO APP
        </button>

        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-5 w-5" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
          <div className="label-eyebrow" style={{ color: "var(--accent)" }}>ADMIN · OWNER-ONLY</div>
        </div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight mb-2">Restore Customer Access</h1>
        <p className="text-sm mb-8 leading-relaxed" style={{ color: "var(--fg-2)" }}>
          Force-syncs a customer&apos;s Stripe subscription state to their TradeGreen account.
          Use when someone paid but their access got locked out. Signed in as{" "}
          <span className="font-mono-num" style={{ color: "var(--fg)" }}>{user?.email}</span>.
        </p>

        <form
          onSubmit={submit}
          className="p-6 space-y-5"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "14px",
            backdropFilter: "blur(12px)",
          }}
        >
          <div>
            <label className="label-eyebrow block mb-2">CUSTOMER&apos;S TRADEGREEN EMAIL <span style={{ color: "var(--loss)" }}>*</span></label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
              required
              data-testid="admin-restore-email"
              autoFocus
              className="w-full px-3 py-2.5 text-sm font-mono-num focus:outline-none transition-colors"
              style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--fg)" }}
            />
            <div className="mt-1.5 text-[11px]" style={{ color: "var(--fg-3)" }}>The email they use to log in to TradeGreen.</div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">STRIPE CUSTOMER ID <span style={{ color: "var(--fg-3)" }}>· optional</span></label>
            <input
              type="text"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="cus_XXXXXXXXXXXXXX"
              data-testid="admin-restore-cid"
              className="w-full px-3 py-2.5 text-sm font-mono-num focus:outline-none transition-colors"
              style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--fg)" }}
            />
            <div className="mt-1.5 text-[11px] leading-relaxed" style={{ color: "var(--fg-3)" }}>
              Only needed when their Stripe email differs from their TradeGreen login.
              Grab it from{" "}
              <a href="https://dashboard.stripe.com/customers" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "var(--accent)" }}>
                dashboard.stripe.com/customers
              </a>.
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !email.trim()}
            data-testid="admin-restore-submit"
            className="w-full py-3 text-sm font-semibold tracking-wide flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "var(--accent)", color: "white", borderRadius: "10px", boxShadow: loading ? "none" : "var(--accent-glow)" }}
          >
            {loading ? (<><RotateCw className="h-4 w-4 animate-spin" /> RECONCILING WITH STRIPE…</>) : "RESTORE FROM STRIPE"}
          </button>
        </form>

        {/* Manual override — bypasses Stripe, always works */}
        <div
          className="mt-6 p-6"
          style={{
            background: "var(--surface)",
            border: "1px dashed rgba(34,197,94,0.35)",
            borderRadius: "14px",
            backdropFilter: "blur(12px)",
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Gift className="h-4 w-4" strokeWidth={1.5} style={{ color: "var(--green)" }} />
            <div className="label-eyebrow" style={{ color: "var(--green)" }}>MANUAL OVERRIDE · BYPASS STRIPE</div>
          </div>
          <p className="text-xs mb-4 leading-relaxed" style={{ color: "var(--fg-2)" }}>
            If Stripe reports the customer as canceled/incomplete/past_due but you want to grant access
            anyway (customer service gesture, disputed charge, etc.), extend their access manually.
            Uses the email above.
          </p>
          <div className="flex gap-3">
            <input
              type="number"
              min="1" max="365"
              value={grantDays}
              onChange={(e) => setGrantDays(e.target.value)}
              data-testid="admin-grant-days"
              className="w-24 px-3 py-2.5 text-sm font-mono-num focus:outline-none"
              style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--fg)" }}
            />
            <button
              type="button"
              onClick={grant}
              disabled={granting || !email.trim() || !grantDays}
              data-testid="admin-grant-submit"
              className="flex-1 py-2.5 text-xs font-semibold tracking-wide flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              style={{ background: "rgba(34,197,94,0.15)", border: "1px solid var(--green)", color: "var(--green)", borderRadius: "10px" }}
            >
              {granting ? "GRANTING…" : `GRANT ${grantDays || "N"} DAYS`}
            </button>
          </div>
        </div>

        {/* Customer Health Check — proactively check any customer's Stripe state.
            Answers: are they on trial? does their card work? are they at risk of
            lockout when trial ends? Use before/after upgrade calls to confirm. */}
        <div
          className="mt-6 p-6"
          style={{
            background: "var(--surface)",
            border: "1px dashed rgba(34,197,94,0.28)",
            borderRadius: "14px",
            backdropFilter: "blur(12px)",
          }}
          data-testid="admin-health-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <HeartPulse className="h-4 w-4" strokeWidth={1.5} style={{ color: "var(--green)" }} />
            <div className="label-eyebrow" style={{ color: "var(--green)" }}>CUSTOMER HEALTH CHECK · LOOK UP ANY EMAIL</div>
          </div>
          <p className="text-xs mb-4 leading-relaxed" style={{ color: "var(--fg-2)" }}>
            Shows local tier + full Stripe subscription state + whether a card is on file. If a
            customer is on a trial with no payment method, they&apos;ll be flagged as{" "}
            <strong style={{ color: "var(--loss)" }}>AT RISK</strong> — meaning they&apos;d get locked
            out at trial end. Fix by opening the Stripe portal or granting manual days.
          </p>
          <div className="flex gap-3 mb-4">
            <input
              type="email"
              value={healthEmail}
              onChange={(e) => setHealthEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") checkHealth(); }}
              placeholder="customer@example.com"
              data-testid="admin-health-email"
              className="flex-1 px-3 py-2.5 text-sm font-mono-num focus:outline-none"
              style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--fg)" }}
            />
            <button
              type="button"
              onClick={checkHealth}
              disabled={checking || !healthEmail.trim()}
              data-testid="admin-health-check"
              className="px-5 py-2.5 text-xs font-semibold tracking-wide flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              style={{ background: "var(--green)", color: "black", borderRadius: "10px" }}
            >
              {checking ? (<><RotateCw className="h-3.5 w-3.5 animate-spin" /> CHECKING…</>) : "RUN CHECK"}
            </button>
          </div>

          {healthResult && (
            <div className="space-y-3 mt-4">
              {/* Risk banner */}
              <div
                className="p-3 flex items-start gap-2"
                style={{
                  background: healthResult.at_risk ? "rgba(239,68,68,0.08)" : "rgba(34,197,94,0.08)",
                  border: `1px solid ${healthResult.at_risk ? "rgba(239,68,68,0.35)" : "rgba(34,197,94,0.35)"}`,
                  borderRadius: "8px",
                }}
                data-testid="admin-health-risk"
              >
                {healthResult.at_risk
                  ? <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--loss)" }} strokeWidth={1.5} />
                  : <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--green)" }} strokeWidth={1.5} />
                }
                <div className="flex-1">
                  <div className="text-xs font-semibold" style={{ color: healthResult.at_risk ? "var(--loss)" : "var(--green)" }}>
                    {healthResult.at_risk ? "AT RISK OF LOCKOUT" : "HEALTHY — no action needed"}
                  </div>
                  {healthResult.risks && healthResult.risks.length > 0 && (
                    <ul className="mt-1 text-xs space-y-0.5" style={{ color: "var(--fg-2)" }}>
                      {healthResult.risks.map((r, i) => <li key={i}>• {r}</li>)}
                    </ul>
                  )}
                </div>
              </div>

              {/* Local + Stripe state */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3" style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "8px" }}>
                  <div className="label-eyebrow mb-2" style={{ color: "var(--fg-3)" }}>LOCAL STATE</div>
                  <HealthRow label="plan_tier" value={healthResult.local_state?.plan_tier} />
                  <HealthRow label="subscription_status" value={healthResult.local_state?.subscription_status} />
                  <HealthRow label="subscribed_until" value={healthResult.local_state?.subscribed_until} />
                  <HealthRow label="trial_ends_at" value={healthResult.local_state?.trial_ends_at} />
                  <HealthRow label="stripe_customer_id" value={healthResult.local_state?.stripe_customer_id} />
                  <HealthRow label="stripe_subscription_id" value={healthResult.local_state?.stripe_subscription_id} />
                  {healthResult.local_state?.is_promo_or_manual_grant && (
                    <div className="mt-1 text-[10px]" style={{ color: "var(--accent)" }}>PROMO/MANUAL GRANT — never expires</div>
                  )}
                </div>
                <div className="p-3" style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "8px" }}>
                  <div className="label-eyebrow mb-2" style={{ color: "var(--fg-3)" }}>STRIPE STATE</div>
                  {healthResult.stripe_subscriptions?.length ? healthResult.stripe_subscriptions.map((s, i) => (
                    <div key={s.id} className="mb-2 pb-2" style={{ borderBottom: i < healthResult.stripe_subscriptions.length - 1 ? "1px solid var(--border)" : "none" }}>
                      <HealthRow label="id" value={s.id} />
                      <HealthRow label="status" value={s.status} highlight={s.status === "active" || s.status === "trialing" ? "good" : (s.status === "past_due" || s.status === "canceled" ? "bad" : null)} />
                      <HealthRow label="trial_end" value={s.trial_end} />
                      <HealthRow label="period_end" value={s.current_period_end} />
                      <HealthRow label="cancel_at_end" value={s.cancel_at_period_end ? "YES" : "no"} highlight={s.cancel_at_period_end ? "bad" : null} />
                      <HealthRow label="card_on_file" value={s.card_last4 ? `****${s.card_last4}` : "NONE"} highlight={s.card_last4 ? "good" : "bad"} />
                    </div>
                  )) : (
                    <div className="text-xs" style={{ color: "var(--fg-3)" }}>No Stripe subscriptions found.</div>
                  )}
                  {healthResult.customer_default_card_last4 && (
                    <HealthRow label="customer_default_card" value={`****${healthResult.customer_default_card_last4}`} highlight="good" />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mint Promo Codes — generate single-use lifetime Pro codes on the fly */}
        <div
          className="mt-6 p-6"
          style={{
            background: "var(--surface)",
            border: "1px dashed rgba(139,92,246,0.35)",
            borderRadius: "14px",
            backdropFilter: "blur(12px)",
          }}
          data-testid="admin-mint-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <Ticket className="h-4 w-4" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            <div className="label-eyebrow" style={{ color: "var(--accent)" }}>MINT PROMO CODE · SINGLE-USE LIFETIME PRO</div>
          </div>
          <p className="text-xs mb-4 leading-relaxed" style={{ color: "var(--fg-2)" }}>
            Generate a fresh <span className="font-mono-num">TGPRO-XXXX-XXXX</span> code that any user can
            redeem on <span className="font-mono-num">/pricing</span> for permanent Pro access. Each code
            is single-use — once redeemed, it&apos;s dead. Great for giveaways, influencer drops, or CS gifts.
          </p>
          <div className="flex gap-3 mb-4">
            <input
              type="text"
              value={mintLabel}
              onChange={(e) => setMintLabel(e.target.value)}
              placeholder="Label (internal, e.g. 'Ross giveaway May')"
              maxLength={80}
              data-testid="admin-mint-label"
              className="flex-1 px-3 py-2.5 text-sm focus:outline-none"
              style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--fg)" }}
            />
            <button
              type="button"
              onClick={mintCode}
              disabled={minting}
              data-testid="admin-mint-submit"
              className="px-5 py-2.5 text-xs font-semibold tracking-wide flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              style={{ background: "var(--accent)", color: "white", borderRadius: "10px", boxShadow: minting ? "none" : "var(--accent-glow)" }}
            >
              {minting ? (<><RotateCw className="h-3.5 w-3.5 animate-spin" /> MINTING…</>) : "MINT NEW CODE"}
            </button>
          </div>

          {mintedList.length > 0 && (
            <div>
              <div className="label-eyebrow mb-2" style={{ color: "var(--fg-3)" }}>RECENT MINTS ({mintedList.length})</div>
              <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="admin-mint-list">
                {mintedList.map((m) => {
                  const redeemed = !!m.redeemed_by;
                  return (
                    <div
                      key={m.code}
                      className="flex items-center gap-3 px-3 py-2 text-xs font-mono-num"
                      style={{
                        background: "var(--bg)",
                        border: `1px solid ${redeemed ? "var(--border)" : "rgba(139,92,246,0.25)"}`,
                        borderRadius: "8px",
                        opacity: redeemed ? 0.55 : 1,
                      }}
                      data-testid={`admin-mint-row-${m.code}`}
                    >
                      <span style={{ color: redeemed ? "var(--fg-3)" : "var(--fg)", flex: 1 }}>{m.code}</span>
                      <span style={{ color: "var(--fg-3)", fontSize: "10px" }}>
                        {redeemed ? `Redeemed · ${(m.redeemed_by_email || "").split("@")[0]}` : (m.label || "").slice(0, 30)}
                      </span>
                      {!redeemed && (
                        <button
                          type="button"
                          onClick={() => copyCode(m.code)}
                          data-testid={`admin-mint-copy-${m.code}`}
                          className="p-1.5 transition-colors"
                          style={{ color: copiedCode === m.code ? "var(--green)" : "var(--fg-3)" }}
                          title="Copy code"
                        >
                          {copiedCode === m.code ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ALL promo-code redemptions (both hardcoded like TGPROFOREVER and minted codes).
              Answers the "did anyone use my code?" question without digging into Mongo. */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow" style={{ color: "var(--fg-3)" }}>
                RECENT REDEMPTIONS ({redemptions.length})
              </div>
              <button
                type="button"
                onClick={loadRedemptions}
                data-testid="admin-redemptions-refresh"
                className="text-[10px] tracking-widest transition-colors"
                style={{ color: "var(--accent)" }}
              >
                REFRESH
              </button>
            </div>
            {redemptions.length === 0 ? (
              <div
                className="px-3 py-3 text-xs font-mono-num text-center"
                style={{ background: "var(--bg)", border: "1px dashed var(--border)", borderRadius: 8, color: "var(--fg-3)" }}
                data-testid="admin-redemptions-empty"
              >
                No promo redemptions yet. When someone uses a code, they&apos;ll appear here.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="admin-redemptions-list">
                {redemptions.map((r, i) => {
                  const when = r.redeemed_at ? new Date(r.redeemed_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "?";
                  return (
                    <div
                      key={`${r.code}-${r.user_id}-${i}`}
                      className="flex items-center gap-3 px-3 py-2 text-xs font-mono-num"
                      style={{
                        background: "var(--bg)",
                        border: "1px solid rgba(62,224,124,0.20)",
                        borderRadius: 8,
                      }}
                      data-testid={`admin-redemption-row-${i}`}
                    >
                      <span style={{ color: "var(--green)", minWidth: 140 }}>{r.code}</span>
                      <span style={{ color: "var(--fg)", flex: 1 }}>
                        {r.user_email || "(unknown user)"}
                        {r.user_name && <span style={{ color: "var(--fg-3)" }}> · {r.user_name}</span>}
                      </span>
                      <span style={{ color: "var(--fg-3)", fontSize: 10, whiteSpace: "nowrap" }}>{when}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Bunny CDN video migration — push existing clips to the fast global
            CDN so playback is snappy (~1-2s to first frame) instead of the
            slower backend stream fallback. Idempotent: clips already on Bunny
            are skipped, so this button is safe to press repeatedly. */}
        <div
          className="mt-6 p-6"
          style={{
            background: "var(--surface)",
            border: "1px dashed rgba(168,85,247,0.32)",
            borderRadius: "14px",
            backdropFilter: "blur(12px)",
          }}
          data-testid="admin-cdn-panel"
        >
          <div className="flex items-center gap-2 mb-1">
            <Zap className="h-4 w-4" style={{ color: "var(--accent)" }} />
            <h2 className="text-base font-medium" style={{ color: "var(--fg)" }}>
              Video CDN (Bunny Stream)
            </h2>
          </div>
          <p className="text-xs mb-4" style={{ color: "var(--fg-3)" }}>
            Push existing trade clips to Bunny&apos;s global edge network so playback loads in 1–2 seconds anywhere in the US
            instead of streaming from our backend. New uploads are pushed automatically — this button backfills your history.
          </p>

          {bunnyStatus && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3" style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8 }}>
                <div className="label-eyebrow" style={{ color: "var(--fg-3)" }}>TOTAL CLIPS</div>
                <div className="text-xl font-mono-num mt-1" style={{ color: "var(--fg)" }} data-testid="admin-cdn-total">
                  {bunnyStatus.total_clips ?? 0}
                </div>
              </div>
              <div className="p-3" style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8 }}>
                <div className="label-eyebrow" style={{ color: "var(--fg-3)" }}>ON CDN</div>
                <div className="text-xl font-mono-num mt-1" style={{ color: "var(--accent)" }} data-testid="admin-cdn-uploaded">
                  {bunnyStatus.uploaded_to_bunny ?? 0}
                </div>
              </div>
              <div className="p-3" style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8 }}>
                <div className="label-eyebrow" style={{ color: "var(--fg-3)" }}>READY TO PLAY</div>
                <div className="text-xl font-mono-num mt-1" style={{ color: "var(--green)" }} data-testid="admin-cdn-ready">
                  {bunnyStatus.ready_on_bunny ?? 0}
                </div>
              </div>
            </div>
          )}

          {bunnyStatus && !bunnyStatus.configured && (
            <div className="p-3 mb-3 text-xs" style={{ background: "rgba(244,63,94,0.10)", border: "1px solid rgba(244,63,94,0.35)", borderRadius: 8, color: "#F43F5E" }}>
              Bunny Stream is not configured on this server. Check the <code>BUNNY_STREAM_*</code> env vars.
            </div>
          )}

          <div className="flex items-center gap-3">
            <label className="text-xs" style={{ color: "var(--fg-3)" }}>Last</label>
            <select
              value={migrateDays}
              onChange={(e) => setMigrateDays(Number(e.target.value))}
              disabled={migrating || !bunnyStatus?.configured}
              data-testid="admin-cdn-days-select"
              className="text-xs font-mono-num px-2 py-1.5 rounded"
              style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--fg)" }}
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={365}>1 year</option>
            </select>
            <button
              onClick={kickoffMigration}
              disabled={migrating || !bunnyStatus?.configured}
              data-testid="admin-cdn-migrate-btn"
              className="px-4 py-1.5 text-xs font-medium tracking-wide transition-colors disabled:opacity-40"
              style={{
                background: migrating ? "var(--bg)" : "var(--accent)",
                color: migrating ? "var(--fg-3)" : "white",
                borderRadius: 8,
                border: "1px solid var(--accent)",
              }}
            >
              {migrating ? "QUEUEING..." : "MIGRATE TO CDN"}
            </button>
            <button
              onClick={refreshBunnyLive}
              data-testid="admin-cdn-refresh"
              className="text-[10px] tracking-widest transition-colors ml-auto"
              style={{ color: "var(--accent)" }}
            >
              REFRESH
            </button>
          </div>
        </div>

        {/* Influencer Partner Program — create referral codes + track commissions */}
        <div
          className="mt-6 p-6"
          style={{
            background: "var(--surface)",
            border: "1px dashed rgba(34,197,94,0.28)",
            borderRadius: "14px",
            backdropFilter: "blur(12px)",
          }}
          data-testid="admin-partners-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <Handshake className="h-4 w-4" strokeWidth={1.5} style={{ color: "var(--green)" }} />
            <div className="label-eyebrow" style={{ color: "var(--green)" }}>INFLUENCER PARTNERS · 20% OFF · 25% RECURRING</div>
          </div>
          <p className="text-xs mb-4 leading-relaxed" style={{ color: "var(--fg-2)" }}>
            Create a referral code for an influencer. Their audience gets{" "}
            <strong style={{ color: "var(--green)" }}>20% off for 3 months</strong>, and they earn{" "}
            <strong style={{ color: "var(--green)" }}>25% of every dollar</strong> their referrals spend
            — forever. Share link:{" "}
            <span className="font-mono-num text-[11px]" style={{ color: "var(--accent)" }}>
              https://trade-green.com/?ref=CODE
            </span>
          </p>

          {/* Create partner form */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <input
              type="text"
              value={newPartner.code}
              onChange={(e) => setNewPartner((p) => ({ ...p, code: e.target.value }))}
              placeholder="CODE (e.g. ROSS20)"
              maxLength={40}
              data-testid="partner-new-code"
              className="px-3 py-2.5 text-sm font-mono-num focus:outline-none uppercase"
              style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--fg)" }}
            />
            <input
              type="text"
              value={newPartner.name}
              onChange={(e) => setNewPartner((p) => ({ ...p, name: e.target.value }))}
              placeholder="Display name (Ross Cameron)"
              data-testid="partner-new-name"
              className="px-3 py-2.5 text-sm focus:outline-none"
              style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--fg)" }}
            />
            <input
              type="email"
              value={newPartner.email}
              onChange={(e) => setNewPartner((p) => ({ ...p, email: e.target.value }))}
              placeholder="Their login email (for dashboard access)"
              data-testid="partner-new-email"
              className="px-3 py-2.5 text-sm focus:outline-none"
              style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--fg)" }}
            />
          </div>
          <button
            type="button"
            onClick={createPartner}
            disabled={creatingPartner}
            data-testid="partner-create-btn"
            className="mb-6 px-5 py-2.5 text-xs font-semibold tracking-wide flex items-center gap-2 transition-all disabled:opacity-50"
            style={{ background: "var(--green)", color: "black", borderRadius: "10px" }}
          >
            {creatingPartner ? (<><RotateCw className="h-3.5 w-3.5 animate-spin" /> CREATING…</>) : "CREATE PARTNER"}
          </button>

          {/* Existing partners list */}
          {partners.length > 0 && (
            <div>
              <div className="label-eyebrow mb-2" style={{ color: "var(--fg-3)" }}>ACTIVE PARTNERS ({partners.length})</div>
              <div className="space-y-2" data-testid="partner-list">
                {partners.map((p) => (
                  <div
                    key={p.code}
                    className="p-3 flex items-center gap-4 flex-wrap"
                    style={{
                      background: "var(--bg)",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                    }}
                    data-testid={`partner-row-${p.code}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold" style={{ color: "var(--fg)" }}>{p.name}</span>
                        <span className="font-mono-num text-[11px] px-1.5 py-0.5" style={{ background: "rgba(168,85,247,0.14)", color: "var(--accent)", borderRadius: "4px" }}>{p.code}</span>
                      </div>
                      <div className="text-[11px] mt-1 font-mono-num" style={{ color: "var(--fg-3)" }}>
                        {p.discount_pct}% off × {p.discount_months}mo · {p.commission_pct}% recurring
                        {p.email ? ` · ${p.email}` : ""}
                      </div>
                    </div>
                    <div className="text-xs font-mono-num text-right">
                      <div style={{ color: "var(--fg)" }}>{p.signup_count} signups</div>
                      <div style={{ color: "var(--green)" }}>${(p.commission_pending || 0).toFixed(2)} pending</div>
                      <div className="text-[10px]" style={{ color: "var(--fg-3)" }}>${(p.commission_paid_out || 0).toFixed(2)} paid</div>
                    </div>
                    {p.commission_pending > 0 && (
                      <button
                        onClick={() => markPartnerPaid(p.code)}
                        data-testid={`partner-mark-paid-${p.code}`}
                        className="shrink-0 px-3 py-1.5 text-[11px] font-semibold tracking-wide flex items-center gap-1"
                        style={{ background: "var(--accent)", color: "white", borderRadius: "6px" }}
                        title="Mark all pending commissions as paid"
                      >
                        <DollarSign className="h-3 w-3" strokeWidth={1.5} /> MARK PAID
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>


        {result && (
          <div
            className="mt-6 p-5"
            data-testid="admin-restore-result"
            style={{
              background: "var(--surface)",
              border: `1px solid ${result.ok ? "rgba(34,197,94,0.35)" : "rgba(244,63,94,0.35)"}`,
              borderRadius: "14px",
              backdropFilter: "blur(12px)",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              {result.ok ? (
                <CheckCircle2 className="h-5 w-5" strokeWidth={1.5} style={{ color: "var(--green)" }} />
              ) : (
                <XCircle className="h-5 w-5" strokeWidth={1.5} style={{ color: "var(--loss)" }} />
              )}
              <div className="label-eyebrow" style={{ color: result.ok ? "var(--green)" : "var(--loss)" }}>
                {result.ok ? "RECONCILED" : `FAILED · ${result.status || "ERROR"}`}
              </div>
            </div>
            {result.ok ? (
              <>
                <div className="space-y-2 text-xs font-mono-num" style={{ color: "var(--fg-2)" }}>
                  <Row label="Email" value={result.data?.email} />
                  <Row label="Has access" value={result.data?.billing?.has_access ? "YES · UNLOCKED" : "NO"}
                       valueColor={result.data?.billing?.has_access ? "var(--green)" : "var(--loss)"} />
                  <Row label="Status" value={result.data?.billing?.status} />
                  <Row label="Subscribed until" value={result.data?.subscribed_until?.slice(0, 10) || "—"} />
                  <Row label="Days remaining" value={result.data?.billing?.days_left_sub ?? result.data?.billing?.days_left_trial ?? "—"} />
                  <Row label="Stripe customer" value={result.data?.stripe_customer_id || "—"} />
                  <Row label="Stripe subscription" value={result.data?.stripe_subscription_id || "—"} />
                </div>

                {/* Raw Stripe snapshot — critical for diagnosing "why isn't he active?" */}
                {result.data?.stripe_subscriptions && result.data.stripe_subscriptions.length > 0 && (
                  <div className="mt-5 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
                    <div className="label-eyebrow mb-3" style={{ color: "var(--fg-3)" }}>ALL STRIPE SUBSCRIPTIONS FOR THIS CUSTOMER</div>
                    <div className="space-y-3">
                      {result.data.stripe_subscriptions.map((s, i) => (
                        <div key={s.id || i} className="text-xs font-mono-num p-3" style={{
                          background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "8px", color: "var(--fg-2)"
                        }}>
                          <Row label="Sub ID" value={s.id} />
                          <Row label="Stripe status" value={s.status}
                               valueColor={
                                 s.status === "active" ? "var(--green)" :
                                 s.status === "trialing" ? "var(--accent)" :
                                 s.status === "past_due" ? "#f59e0b" :
                                 "var(--loss)"
                               } />
                          <Row label="Period ends" value={s.current_period_end?.slice(0, 10) || "—"} />
                          <Row label="Cancels at period end" value={s.cancel_at_period_end ? "YES" : "no"}
                               valueColor={s.cancel_at_period_end ? "var(--loss)" : "var(--fg-2)"} />
                          <Row label="Trial ends" value={s.trial_end?.slice(0, 10) || "—"} />
                          <Row label="Created" value={s.created?.slice(0, 10) || "—"} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {result.data?.stripe_subscriptions && result.data.stripe_subscriptions.length === 0 && (
                  <div className="mt-4 text-xs" style={{ color: "var(--fg-3)" }}>
                    ⚠️ No subscriptions found on this Stripe customer. They may have made a one-time payment,
                    or the customer ID doesn&apos;t match. Use manual override above to grant access.
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs" style={{ color: "var(--fg-2)" }}>{result.error}</p>
            )}
          </div>
        )}

        <div className="mt-6 text-[11px] leading-relaxed" style={{ color: "var(--fg-3)" }}>
          If you see &quot;Not authorized&quot;, add your email to <code>ADMIN_EMAILS</code> in your production env vars
          (Emergent dashboard → env config → <code>ADMIN_EMAILS=you@example.com</code>).
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, valueColor }) => (
  <div className="flex justify-between gap-4 py-1.5" style={{ borderBottom: "1px solid var(--border)" }}>
    <span style={{ color: "var(--fg-3)" }}>{label}</span>
    <span style={{ color: valueColor || "var(--fg)" }} className="text-right break-all">{String(value)}</span>
  </div>
);


// Compact key/value row used inside the Customer Health card. `highlight`
// tints the value green/red based on whether the field indicates good/bad state.
const HealthRow = ({ label, value, highlight }) => (
  <div className="flex justify-between gap-3 text-[11px] py-0.5 font-mono-num">
    <span style={{ color: "var(--fg-3)" }}>{label}</span>
    <span
      className="text-right break-all"
      style={{ color: highlight === "good" ? "var(--green)" : highlight === "bad" ? "var(--loss)" : "var(--fg)" }}
    >
      {value == null || value === "" ? "—" : String(value)}
    </span>
  </div>
);
